import { createFileRoute, Link } from "@tanstack/react-router";
import { Navbar } from "@/components/Navbar";
import { useI18n } from "@/lib/i18n";
import { pageHead } from "@/lib/seo";

export const Route = createFileRoute("/privacy")({
  head: () => pageHead({
    path: "/privacy",
    title: "Privacy Policy — Book Swap",
    description: "How Book Swap collects, uses and protects your data, why locations are blurred, and how to request deletion of your account.",
  }),
  component: Privacy,
});

type Section = { en: { title: string; body: string }; ar: { title: string; body: string } };

const sections: Section[] = [
  {
    en: { title: "What we collect", body: "When you create an account we collect your name, email, mobile number, neighborhood-level address, and approximate location coordinates. We also store the books you list, messages you send, and your reading interests." },
    ar: { title: "ما نقوم بجمعه", body: "عند إنشاء حسابك نقوم بجمع الاسم والبريد الإلكتروني ورقم الجوال والعنوان على مستوى الحي والإحداثيات التقريبية. كما نحتفظ بالكتب التي تعرضها والرسائل التي ترسلها واهتماماتك القرائية." },
  },
  {
    en: { title: "Why we collect it", body: "We use this information to show you books available near you, let neighbors message you about your listings, and keep the community safe through trust signals like exchange history." },
    ar: { title: "لماذا نجمعها", body: "نستخدم هذه المعلومات لعرض الكتب المتاحة بالقرب منك، وللسماح للجيران بمراسلتك بشأن عروضك، وللحفاظ على أمان المجتمع من خلال إشارات الثقة مثل سجل التبادلات." },
  },
  {
    en: { title: "How your location is used", body: "Your coordinates are rounded before storage and only ever displayed at neighborhood granularity. Other users never see your exact address — only an approximate distance." },
    ar: { title: "كيف يُستخدم موقعك", body: "يتم تقريب إحداثياتك قبل التخزين ولا يتم عرضها إلا على مستوى الحي. لا يرى المستخدمون الآخرون عنوانك الدقيق — فقط مسافة تقريبية." },
  },
  {
    en: { title: "What we never do", body: "We never sell your personal data. We never share your contact details with third parties for marketing. We never expose your exact home address to other users." },
    ar: { title: "ما لا نفعله أبدًا", body: "لا نبيع بياناتك الشخصية أبدًا. لا نشارك بيانات التواصل الخاصة بك مع أطراف ثالثة لأغراض التسويق. لا نُظهر عنوان منزلك الدقيق للمستخدمين الآخرين." },
  },
  {
    en: { title: "Data retention", body: "We keep your account data for as long as your account is active. If you delete your account, your personal information is removed within 30 days. Some anonymized usage data may be retained for analytics." },
    ar: { title: "الاحتفاظ بالبيانات", body: "نحتفظ ببيانات حسابك ما دام الحساب نشطًا. عند حذف حسابك يتم إزالة معلوماتك الشخصية خلال 30 يومًا. قد نحتفظ ببعض البيانات المجهّلة للتحليلات." },
  },
  {
    en: { title: "Your rights", body: "You can view, update, or delete your personal information at any time from your profile page. You may also request a copy of your data or full deletion by contacting us." },
    ar: { title: "حقوقك", body: "يمكنك عرض معلوماتك الشخصية أو تحديثها أو حذفها في أي وقت من صفحة ملفك الشخصي. يمكنك أيضًا طلب نسخة من بياناتك أو حذفها بالكامل بالتواصل معنا." },
  },
  {
    en: { title: "Cookies and storage", body: "We use browser storage to remember your sign-in session, language preference, and theme. We do not use third-party advertising trackers." },
    ar: { title: "ملفات تعريف الارتباط والتخزين", body: "نستخدم تخزين المتصفح لتذكر جلسة تسجيل الدخول واللغة والمظهر. لا نستخدم أدوات تتبع إعلانية من أطراف ثالثة." },
  },
  {
    en: { title: "Security of your data", body: "We use industry-standard safeguards, encrypted connections, and database-level access rules to protect your information. No online service can promise perfect security, and we cannot guarantee that unauthorized access will never occur. You are responsible for keeping your password and one-time codes confidential." },
    ar: { title: "أمان بياناتك", body: "نستخدم إجراءات حماية معيارية واتصالات مشفّرة وقواعد وصول على مستوى قاعدة البيانات لحماية معلوماتك. لا يمكن لأي خدمة إلكترونية أن تضمن أمانًا مطلقًا، ولا يمكننا ضمان عدم حدوث وصول غير مصرح به إطلاقًا. أنت مسؤول عن الحفاظ على سرية كلمة المرور ورموز التحقق." },
  },
  {
    en: { title: "Information you share with other users", body: "Anything you choose to share inside chats, listings, reviews, or meeting arrangements is shared at your own risk. Once you send your phone number, address, or meeting details to another member, Book Swap cannot control how that person uses or stores it." },
    ar: { title: "المعلومات التي تشاركها مع المستخدمين الآخرين", body: "أي معلومات تختار مشاركتها في المحادثات أو العروض أو التقييمات أو ترتيبات اللقاء تتم على مسؤوليتك الخاصة. بمجرد إرسال رقم هاتفك أو عنوانك أو تفاصيل اللقاء إلى عضو آخر، لا تستطيع Book Swap التحكم في كيفية استخدامه أو حفظه لتلك المعلومات." },
  },
  {
    en: { title: "Third-party services", body: "We rely on trusted providers for hosting, database and authentication, email delivery, map tiles, and approximate location lookup. These providers process limited data strictly to run the service. Book Swap is not responsible for the independent practices of any third-party site you reach through a link." },
    ar: { title: "خدمات الأطراف الثالثة", body: "نعتمد على مزودين موثوقين للاستضافة وقاعدة البيانات والمصادقة وإرسال البريد وخرائط الموقع وتحديد الموقع التقريبي. يعالج هؤلاء المزودون بيانات محدودة لتشغيل الخدمة فقط. لا تتحمل Book Swap مسؤولية ممارسات أي موقع خارجي تصل إليه عبر رابط." },
  },
  {
    en: { title: "Exchanges happen between users", body: "Book Swap is a platform that helps neighbors find each other. We are not a party to any swap, loan, or donation. We do not own, inspect, insure, or deliver any book, and we are not responsible for books that are damaged, lost, stolen, never returned, or misrepresented. Any dispute is between the members involved." },
    ar: { title: "التبادلات تتم بين المستخدمين", body: "Book Swap منصة تساعد الجيران على التواصل. نحن لسنا طرفًا في أي تبادل أو إعارة أو تبرع. لا نملك أي كتاب ولا نفحصه ولا نؤمّن عليه ولا نوصله، ولا نتحمل مسؤولية الكتب التالفة أو المفقودة أو المسروقة أو التي لم تُعَد أو التي وُصفت بشكل غير دقيق. أي نزاع يكون بين الأعضاء المعنيين." },
  },
  {
    en: { title: "Children", body: "Book Swap is intended for users aged 18 and older. We do not knowingly collect data from children." },
    ar: { title: "الأطفال", body: "خدمة Book Swap مخصصة للمستخدمين من سن 18 عامًا فأكثر. لا نقوم عن قصد بجمع بيانات من الأطفال." },
  },
  {
    en: { title: "Changes to this policy", body: "We may update this policy from time to time. The latest version will always be posted on this page." },
    ar: { title: "تعديل هذه السياسة", body: "قد نقوم بتحديث هذه السياسة من وقت لآخر. ستجد دائمًا أحدث نسخة على هذه الصفحة." },
  },
  {
    en: { title: "Contact us", body: "For privacy questions or data requests, please reach us through the contact page." },
    ar: { title: "التواصل معنا", body: "لأي استفسارات تتعلق بالخصوصية أو طلبات البيانات، يرجى التواصل معنا عبر صفحة التواصل." },
  },
];

function Privacy() {
  const { lang } = useI18n();
  const isAr = lang === "ar";

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="mx-auto max-w-3xl px-4 py-10" dir={isAr ? "rtl" : "ltr"}>
        <div className="rounded-3xl p-8 text-white shadow-[var(--shadow-elegant)]" style={{ background: "var(--gradient-hero)" }}>
          <p className="text-sm text-white/80">Book Swap</p>
          <h1 className="mt-1 font-serif text-3xl text-white md:text-4xl">
            {isAr ? "سياسة الخصوصية" : "Privacy Policy"}
          </h1>
          <p className="mt-2 text-white/80">
            {isAr ? "النسخة 1.0 — آخر تحديث: مايو 2026" : "Version 1.0 — Last updated: May 2026"}
          </p>
        </div>

        <div className="mt-8 space-y-6 rounded-2xl bg-card p-6 shadow-[var(--shadow-card)] leading-loose text-foreground">
          {sections.map((s, i) => {
            const node = isAr ? s.ar : s.en;
            return (
              <section key={i}>
                <h2 className="font-serif text-lg font-semibold text-primary-dark">
                  {i + 1}. {node.title}
                </h2>
                <p className="mt-2 text-sm text-muted-foreground">{node.body}</p>
              </section>
            );
          })}
          <p className="pt-4 text-sm">
            <Link to="/contact" className="text-primary underline hover:text-primary/80">
              {isAr ? "صفحة التواصل" : "Contact page"}
            </Link>
            <span className="mx-2 text-muted-foreground">·</span>
            <Link to="/terms" className="text-primary underline hover:text-primary/80">
              {isAr ? "شروط الاستخدام" : "Terms of Use"}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
